import { useEffect, useState } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { Skeleton } from '@mui/material';
import { Stack } from '@mui/material';
import { useTranslation } from "react-i18next";

am4core.useTheme(am4themes_animated);

const ChartSix = ({ data, name, className }) => {
    const [showLoader, setShowLoader] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        // Define a custom color palette
        const colors = [
            am4core.color("#6794dc"),
            am4core.color("#F7DBA7"),
            am4core.color("#008080"),
            am4core.color("#dc6967"),
            am4core.color("#FFA500"),
        ];

        // Create chart instance
        let chart = am4core.create(name, am4charts.XYChart);

        chart.events.on('ready', () => {
            setShowLoader(false);
        });

        // Add data
        chart.data = data['data'];
        let legend = data['legend'];

        // Create axes
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "year";
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 10;

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.renderer.labels.template.disabled = false;
        valueAxis.renderer.inside = false;
        valueAxis.extraMax = 0.1;
        valueAxis.title.text = t('LOCAL_INDICATORS.IN_BILLION_ALL');

        // Create stacked series with custom colors
        createSeries("real_estate_tax", legend['real_estate_tax'], colors[0], 'real_estate_tax_percentage');
        createSeries("infrastructure_impact_tax", legend['infrastructure_impact_tax'], colors[1], 'infrastructure_impact_tax_percentage');
        createSeries("local_service_charges", legend['local_service_charges'], colors[2], 'local_service_charges_percentage');
        createSeries("small_business_profit_tax", legend['small_business_profit_tax'], colors[3], 'small_business_profit_tax_percentage');
        createSeries("other_local_income", legend['other_local_income'], colors[4], 'other_local_income_percentage');

        // Create total fiscal income line series
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = "total_fiscal_local_income";
        series.dataFields.categoryX = "year";
        series.tooltipText = "{total_fiscal_local_income}";
        series.tensionX = 1;
        series.stroke = am4core.color("#000"); // Set line color
        series.legendSettings.labelText = legend['total_fiscal_local_income'];
        series.strokeWidth = 2;

        // Add bullets at data points for the line series
        let bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.radius = 5;
        bullet.circle.strokeWidth = 3;
        bullet.fill = am4core.color("#000");

        // Label for the line series bullet
        let label = bullet.createChild(am4core.Label);
        label.fill = am4core.color("#000");
        label.text = "{total_fiscal_local_income}";
        label.horizontalCenter = "middle";
        label.verticalCenter = "bottom";
        label.dy = -10;

        // Function to create a stacked series with a specific color
        function createSeries(field, name, color, percentage) {
            let series = chart.series.push(new am4charts.ColumnSeries());
            series.name = name;
            series.dataFields.valueY = field;
            series.dataFields.categoryX = "year";
            series.sequencedInterpolation = true;
            series.stacked = true;
            series.columns.template.width = am4core.percent(60);
            series.columns.template.tooltipText = `[bold]{name}[/]\n[font-size:14px]{categoryX}: {valueY} ({${percentage}}%)`;
            series.fill = color; // Set the fill color for this series
            series.stroke = color; // Set the stroke color for this series

            let labelBullet = series.bullets.push(new am4charts.LabelBullet());
            labelBullet.label.text = "{valueY}";
            labelBullet.locationY = 0.5;

            return series;
        }

        // Add legend
        chart.legend = new am4charts.Legend();

        // Enable exporting menu
        chart.exporting.menu = new am4core.ExportMenu();
        chart.exporting.menu.align = 'left';
        chart.exporting.menu.verticalAlign = 'top';

        return () => {
            chart.dispose();
        };
    }, []);

    return (
        <>
            <div style={{ display: 'flex', gap: '20px' }}>
                {/* Chart container */}
                <div id={name} style={{ width: showLoader ? '0' : '100%', height: showLoader ? '0' : '700px' }} className={className}></div>
            </div>

            {showLoader && (
                <Stack spacing={1}>
                    <Skeleton variant='text' />
                    <Skeleton variant='rounded' width={'auto'} height={500} />
                </Stack>
            )}
        </>
    );
};

export default ChartSix;
